import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download } from "lucide-react";

export const Route = createFileRoute("/admin/bets")({
  head: () => ({ meta: [{ title: "Bets — Admin" }] }),
  component: AdminBetsPage,
});


type BetType = "all" | "jodi" | "haruf" | "crossing";

// Currently the schema stores only 0-99 single-number bets (Jodi-style).
// We derive bet type from the number for future compatibility.
function inferType(n: number): "jodi" | "haruf" | "crossing" {
  if (n >= 0 && n <= 9) return "haruf";
  return "jodi";
}

function fmtIST(ts: string) {
  return new Date(ts).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
}

function todayIST(): string {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
}

function downloadCSV(filename: string, rows: (string | number)[][]) {
  const csv = rows
    .map((r) => r.map((c) => {
      const s = String(c ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function AdminBetsPage() {
  const qc = useQueryClient();

  useEffect(() => {
    const ch = supabase.channel("admin-bets-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "bets" }, () => {
        qc.invalidateQueries({ queryKey: ["admin_bets_all"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [qc]);

  const { data: games } = useQuery({
    queryKey: ["admin_games_list"],
    queryFn: async () => (await supabase.from("games").select("id, name").order("name")).data ?? [],
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-black">Bets Report</h2>
        <p className="text-xs text-muted-foreground">User-wise and number-wise betting analytics.</p>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:w-[400px]">
          <TabsTrigger value="users">User Bets</TabsTrigger>
          <TabsTrigger value="chart">Number Chart</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <UserBetsTab games={games ?? []} />
        </TabsContent>
        <TabsContent value="chart" className="mt-4">
          <NumberChartTab games={games ?? []} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ============================== TAB 1 ============================== */

function UserBetsTab({ games }: { games: { id: string; name: string }[] }) {
  const [date, setDate] = useState<string>(todayIST());
  const [gameId, setGameId] = useState<string>("");
  const [q, setQ] = useState("");

  const enabled = !!date && !!gameId;

  const { data: rows, isLoading, isError, error } = useQuery({
    queryKey: ["admin_bets_all", date, gameId],
    enabled,
    queryFn: async () => {
      const { data: bets, error: betsErr } = await supabase
        .from("bets")
        .select("id, user_id, number, amount, created_at, status, game_id")
        .eq("bet_date", date)
        .eq("game_id", gameId)
        .neq("status", "reversed")
        .order("created_at", { ascending: false })
        .limit(20000);
      if (betsErr) throw betsErr;
      const list = bets ?? [];
      const userIds = Array.from(new Set(list.map((b: any) => b.user_id)));
      let profileMap = new Map<string, { name: string | null; phone: string | null }>();
      if (userIds.length) {
        const { data: profs, error: profErr } = await supabase
          .from("profiles")
          .select("id, name, phone")
          .in("id", userIds);
        if (profErr) throw profErr;
        profileMap = new Map((profs ?? []).map((p: any) => [p.id, { name: p.name, phone: p.phone }]));
      }
      return list.map((b: any) => ({ ...b, profiles: profileMap.get(b.user_id) ?? { name: null, phone: null } }));
    },
  });


  const marketName = games.find((g) => g.id === gameId)?.name ?? "";

  type UserGroup = {
    user_id: string;
    name: string;
    phone: string;
    numbers: Map<number, number>;
    total: number;
    firstAt: string;
    lastAt: string;
  };

  const grouped = useMemo<UserGroup[]>(() => {
    const map = new Map<string, UserGroup>();
    for (const r of rows ?? []) {
      const anyR = r as any;
      let g = map.get(anyR.user_id);
      if (!g) {
        g = {
          user_id: anyR.user_id,
          name: anyR.profiles?.name ?? "—",
          phone: anyR.profiles?.phone ?? "",
          numbers: new Map(),
          total: 0,
          firstAt: anyR.created_at,
          lastAt: anyR.created_at,
        };
        map.set(anyR.user_id, g);
      }
      const amt = Number(anyR.amount);
      g.numbers.set(anyR.number, (g.numbers.get(anyR.number) ?? 0) + amt);
      g.total += amt;
      if (anyR.created_at < g.firstAt) g.firstAt = anyR.created_at;
      if (anyR.created_at > g.lastAt) g.lastAt = anyR.created_at;
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [rows]);

  const filtered = useMemo(() => {
    if (!q) return grouped;
    const s = q.toLowerCase();
    return grouped.filter(
      (g) => g.name.toLowerCase().includes(s) || g.phone.toLowerCase().includes(s),
    );
  }, [grouped, q]);

  const marketTotal = filtered.reduce((s, g) => s + g.total, 0);

  function exportCSV() {
    const header = ["Player", "Mobile", "Market", "Number", "Amount", "User Total", "Date/Time (IST)"];
    const body: (string | number)[][] = [];
    for (const g of filtered) {
      const nums = Array.from(g.numbers.entries()).sort((a, b) => a[0] - b[0]);
      for (const [n, amt] of nums) {
        body.push([g.name, g.phone, marketName, String(n).padStart(2, "0"), amt, g.total, fmtIST(g.lastAt)]);
      }
    }
    downloadCSV(`user-bets-${date}-${marketName || "market"}.csv`, [header, ...body]);
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-1 gap-2 rounded-xl border bg-card p-3 sm:grid-cols-2 md:grid-cols-4">
        <FilterField label="Date">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </FilterField>
        <FilterField label="Market">
          <Select value={gameId} onValueChange={setGameId}>
            <SelectTrigger><SelectValue placeholder="Select market…" /></SelectTrigger>
            <SelectContent>
              {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Search (name / mobile)">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} disabled={!enabled} />
        </FilterField>
        <div className="flex items-end">
          <Button onClick={exportCSV} variant="outline" className="w-full gap-2" disabled={filtered.length === 0}>
            <Download className="h-4 w-4" /> CSV
          </Button>
        </div>
      </div>

      {!enabled ? (
        <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
          Please select both a date and a market to load the report.
        </div>
      ) : isLoading ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
      ) : isError ? (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-center text-sm text-destructive">
          Failed to load bets. {(error as Error)?.message ?? ""}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
          No user bets found for the selected date and market.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            <SummaryCard label="Market" value={marketName} />
            <SummaryCard label="Total Users" value={filtered.length.toLocaleString("en-IN")} />
            <SummaryCard label="Market Total" value={`₹${marketTotal.toLocaleString("en-IN")}`} highlight />
          </div>

          <div className="grid gap-2 md:grid-cols-2">
            {filtered.map((g) => {
              const nums = Array.from(g.numbers.entries()).sort((a, b) => a[0] - b[0]);
              return (
                <div key={g.user_id} className="rounded-xl border bg-card p-3 shadow-card">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold">{g.name}</p>
                      <p className="text-[11px] text-muted-foreground">{g.phone}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-[10px] font-bold uppercase text-muted-foreground">User Total</p>
                      <p className="text-sm font-black text-primary">₹{g.total.toLocaleString("en-IN")}</p>
                    </div>
                  </div>
                  <p className="mt-1 text-[11px]"><span className="text-muted-foreground">Market:</span> <span className="font-semibold">{marketName}</span></p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {nums.map(([n, amt]) => (
                      <span key={n} className="rounded-md border bg-muted/40 px-2 py-1 text-xs font-semibold">
                        {String(n).padStart(2, "0")} — ₹{amt.toLocaleString("en-IN")}
                      </span>
                    ))}
                  </div>
                  <p className="mt-2 text-[10px] text-muted-foreground">{fmtIST(g.lastAt)} IST</p>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ============================== TAB 2 ============================== */

function NumberChartTab({ games }: { games: { id: string; name: string }[] }) {
  const [date, setDate] = useState<string>(todayIST());
  const [gameId, setGameId] = useState<string>("all");
  const [betType, setBetType] = useState<BetType>("all");

  const { data: rows, isLoading } = useQuery({
    queryKey: ["admin_bets_chart", date, gameId],
    queryFn: async () => {
      let query = supabase
        .from("bets")
        .select("user_id, number, amount, game_id, status")
        .neq("status", "reversed")
        .limit(20000);
      if (date) query = query.eq("bet_date", date);
      if (gameId !== "all") query = query.eq("game_id", gameId);
      const { data } = await query;
      return data ?? [];
    },
  });

  const filteredRows = useMemo(
    () => (rows ?? []).filter((r: any) => betType === "all" || inferType(r.number) === betType),
    [rows, betType]
  );

  const range = useMemo(() => {
    if (betType === "haruf") return { from: 0, to: 9, pad: 1 };
    // jodi / crossing / all → 0..99
    return { from: 0, to: 99, pad: 2 };
  }, [betType]);

  const agg = useMemo(() => {
    const map = new Map<number, { amt: number; users: Set<string> }>();
    for (let n = range.from; n <= range.to; n++) map.set(n, { amt: 0, users: new Set() });
    for (const r of filteredRows) {
      const cur = map.get(r.number);
      if (!cur) continue;
      cur.amt += Number(r.amount);
      cur.users.add(r.user_id);
    }
    return Array.from(map.entries()).map(([n, v]) => ({ n, amt: v.amt, users: v.users.size }));
  }, [filteredRows, range]);

  const totalAmt = agg.reduce((s, x) => s + x.amt, 0);
  const totalUsers = new Set(filteredRows.map((r: any) => r.user_id)).size;
  const maxEntry = agg.reduce((m, x) => (x.amt > m.amt ? x : m), { n: -1, amt: -1, users: 0 });
  const zeroCount = agg.filter((x) => x.amt === 0).length;

  function exportCSV() {
    const header = ["Number", "Total Amount", "Users"];
    const body = agg.map((x) => [String(x.n).padStart(range.pad, "0"), x.amt, x.users]);
    downloadCSV(`number-chart-${date}.csv`, [header, ...body]);
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <SummaryCard label="Total Amount" value={`₹${totalAmt.toLocaleString("en-IN")}`} highlight />
        <SummaryCard label="Unique Users" value={totalUsers.toLocaleString("en-IN")} />
        <SummaryCard
          label="Highest Bet"
          value={maxEntry.amt > 0 ? `${String(maxEntry.n).padStart(range.pad, "0")} · ₹${maxEntry.amt.toLocaleString("en-IN")}` : "—"}
        />
        <SummaryCard label="Zero-Bet Numbers" value={String(zeroCount)} />
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 gap-2 rounded-xl border bg-card p-3 md:grid-cols-4">
        <FilterField label="Date">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </FilterField>
        <FilterField label="Game">
          <Select value={gameId} onValueChange={setGameId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Games</SelectItem>
              {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Bet Type">
          <Select value={betType} onValueChange={(v) => setBetType(v as BetType)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="jodi">Jodi (00–99)</SelectItem>
              <SelectItem value="haruf">Haruf (0–9)</SelectItem>
              <SelectItem value="crossing">Crossing</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>
        <div className="flex items-end">
          <Button onClick={exportCSV} variant="outline" className="w-full gap-2" disabled={totalAmt === 0}>
            <Download className="h-4 w-4" /> CSV
          </Button>
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
      ) : totalAmt === 0 ? (
        <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
          No bets found for the selected filters.
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-1.5 rounded-xl border bg-card p-3 sm:grid-cols-6 md:grid-cols-10">
          {agg.map((x) => {
            const isMax = x.amt > 0 && x.n === maxEntry.n;
            const isZero = x.amt === 0;
            return (
              <div
                key={x.n}
                className={`rounded-md border px-2 py-1.5 text-center transition ${
                  isMax
                    ? "border-primary bg-primary/10 shadow-sm ring-1 ring-primary"
                    : isZero
                    ? "bg-muted/30 opacity-60"
                    : "bg-muted/40"
                }`}
              >
                <div className="text-[10px] font-bold text-muted-foreground">
                  {String(x.n).padStart(range.pad, "0")}
                </div>
                <div className={`text-xs font-black ${isMax ? "text-primary" : ""}`}>
                  ₹{x.amt.toLocaleString("en-IN")}
                </div>
                <div className="text-[9px] text-muted-foreground">{x.users} user{x.users === 1 ? "" : "s"}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============================== Helpers ============================== */

function SummaryCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 ${highlight ? "bg-primary/5 border-primary/30" : "bg-card"}`}>
      <p className="text-[10px] font-bold uppercase text-muted-foreground">{label}</p>
      <p className={`mt-0.5 text-sm font-black md:text-base ${highlight ? "text-primary" : ""}`}>{value}</p>
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
